import { cn } from '@wellesley/ui'
import { UserFull } from '@wellesley/openapi'
import useFollowers from 'hooks/useFollowers'
import { UserAvatar } from '@wellesley/user'
import useActionGuard from 'hooks/useActionGuard'
import { getUserImage } from 'helpers/getUserImage'
import { FC, useState } from 'react'
import { FollowersModalProps } from 'components/FollowersModal/types'
import {
  ConnectionsTabItem,
  ConnectionsTabs,
} from 'components/modals/ConnectionsModal/types'
import pluralize from 'helpers/pluralize'
import UsersPreviewSkeleton from 'components/Skeletons/UsersPreviewSkeleton'

type CmpProps = {
  user: UserFull
  className?: string
  isChannel?: boolean
  showEmpty?: boolean
  renderModal: (props: FollowersModalProps) => React.ReactNode
  onFollow?(): void
  onUnfollow?(): void
}

type FollowersState = {
  tab: ConnectionsTabItem
  open: boolean
}

const UserFollowers: FC<CmpProps> = ({
  user,
  isChannel,
  showEmpty = true,
  className,
  renderModal,
  ...modalProps
}) => {
  const { followers, isLoading } = useFollowers({
    user,
  })

  const [followersState, setFollowersState] = useState<FollowersState>({
    open: false,
    tab: ConnectionsTabs.first,
  })

  const { guardedAction, ActionTooltipWrapper } = useActionGuard()

  const openFollowersTabs = (tab: ConnectionsTabItem) => {
    setFollowersState({
      tab,
      open: true,
    })
  }

  const count = user.stats?.followers || 0
  const firstThreeFollowers = count > 0 ? followers?.slice(0, 3) || [] : []

  if (isLoading)
    return (
      <div className="mb-s flex items-center justify-center">
        <UsersPreviewSkeleton count={3} />
      </div>
    )

  if (count === 0 && !showEmpty) return null

  return (
    <div data-testid="user-followers">
      {followersState.open &&
        renderModal?.({
          user,
          tab: followersState.tab,
          isOpen: true,
          onClose: () =>
            setFollowersState((state) => ({ ...state, open: false })),
          ...modalProps,
        })}
      <ActionTooltipWrapper>
        <div
          onClick={guardedAction(() =>
            openFollowersTabs(ConnectionsTabs.first),
          )}
          className={cn(
            'mb-s flex cursor-pointer items-center text-base font-medium text-gray-900',
            className,
          )}
        >
          {firstThreeFollowers.length > 0 && (
            <ul className="mr-xxs flex items-center">
              {firstThreeFollowers.map((f, i) => (
                <li key={f.id} className={cn('relative', i > 0 && '-ml-xxs')}>
                  <div className="rounded-full bg-white p-0.5">
                    <UserAvatar
                      size={24}
                      name={f.displayName}
                      imgUrl={getUserImage({ user: f })}
                    />
                  </div>
                </li>
              ))}
            </ul>
          )}
          {count} {pluralize(isChannel ? 'subscriber' : 'follower', count)}
        </div>
      </ActionTooltipWrapper>
    </div>
  )
}

export default UserFollowers
